import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { SparkIcon, TooltipTrigger } from '@spark';
import { BUSINESS_GOAL_PILLARS } from './constraintOptions';

const SLIDE_DURATION_MS = 220;
const SLIDE_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

/**
 * Drag-to-rank pillar cards on a least-to-most-important axis
 * ("Define & Prioritize Business Goals", Figma 3047-4189).
 *
 * Uses native HTML5 drag & drop: only 4 fixed cards need reordering, so a
 * DnD library would be overkill. Cards reorder live while dragging and slide
 * into place with a FLIP animation (capture positions before the reorder,
 * then animate each card from its old position to its new one).
 */
const PriorityRanking = ({ order, onChange }) => {
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const cardRefs = useRef(new Map());
    const rectsBeforeReorder = useRef(null);

    const cards = useMemo(
        () =>
            order
                .map((id) => BUSINESS_GOAL_PILLARS.find((p) => p.id === id))
                .filter(Boolean),
        [order]
    );

    // Move the dragged card to the target's slot: dragging rightward lands
    // after the target, dragging leftward lands before it — so the dragged
    // card can occupy any position, including the outermost ones.
    const reorder = (from, to) => {
        const rects = new Map();
        cardRefs.current.forEach((el, id) => rects.set(id, el.getBoundingClientRect()));
        rectsBeforeReorder.current = rects;
        const next = [...order];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        onChange(next);
    };

    // FLIP: right after a reorder re-renders, start each displaced card at its
    // pre-reorder position and slide it to its new slot.
    useLayoutEffect(() => {
        const prevRects = rectsBeforeReorder.current;
        rectsBeforeReorder.current = null;
        if (!prevRects) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        cardRefs.current.forEach((el, id) => {
            const prev = prevRects.get(id);
            if (!prev) return;
            const dx = prev.left - el.getBoundingClientRect().left;
            if (!dx) return;
            el.animate(
                [{ transform: `translateX(${dx}px)` }, { transform: 'translateX(0)' }],
                { duration: SLIDE_DURATION_MS, easing: SLIDE_EASING }
            );
        });
    }, [order]);

    const handleDrop = (targetId) => {
        if (!draggedId || draggedId === targetId) return;
        const from = order.indexOf(draggedId);
        const to = order.indexOf(targetId);
        if (from < 0 || to < 0) return;
        reorder(from, to);
    };

    // Keyboard alternative to drag & drop: ArrowRight/ArrowLeft move the
    // focused card one slot toward "most"/"least" important. Focus follows
    // the card because React reorders the keyed nodes instead of remounting.
    const handleCardKeyDown = (e, pillarId) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const from = order.indexOf(pillarId);
        const to = from + (e.key === 'ArrowRight' ? 1 : -1);
        if (from < 0 || to < 0 || to >= order.length) return;
        reorder(from, to);
    };

    return (
        <div className="cw-priority">
            <div className="cw-priority__axis" aria-hidden="true" />
            <div className="cw-priority__row">
                <div className="cw-priority__endpoint">
                    <span className="cw-priority__endpoint-dot" aria-hidden="true" />
                    <span className="cw-priority__endpoint-label">Least Important</span>
                </div>
                {cards.map((pillar, index) => (
                    <TooltipTrigger
                        key={pillar.id}
                        text={pillar.tooltip}
                        disabled={Boolean(draggedId)}
                    >
                        <div
                            ref={(el) => {
                                if (el) cardRefs.current.set(pillar.id, el);
                                else cardRefs.current.delete(pillar.id);
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label={`${pillar.title}, priority ${index + 1} of ${cards.length}. Use the left and right arrow keys to change its priority.`}
                            onKeyDown={(e) => handleCardKeyDown(e, pillar.id)}
                            className={[
                                'cw-priority__card',
                                draggedId === pillar.id ? 'cw-priority__card--dragging' : '',
                                dragOverId === pillar.id && draggedId !== pillar.id
                                    ? 'cw-priority__card--drag-over'
                                    : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'move';
                                try {
                                    e.dataTransfer.setData('text/plain', pillar.id);
                                } catch {
                                    // IE compat no-op
                                }
                                // The browser snapshots the drag ghost after this
                                // handler returns, and React re-renders before that
                                // snapshot — hiding the card synchronously would
                                // produce a blank ghost. Defer one task so the
                                // snapshot captures the fully visible card.
                                window.setTimeout(() => setDraggedId(pillar.id), 0);
                            }}
                            onDragEnd={() => {
                                setDraggedId(null);
                                setDragOverId(null);
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                if (!draggedId || draggedId === pillar.id) return;
                                const from = order.indexOf(draggedId);
                                const to = order.indexOf(pillar.id);
                                if (from < 0 || to < 0) return;
                                // Reorder live, but only once the pointer crosses the
                                // hovered card's midpoint in the direction of travel —
                                // otherwise cards would oscillate under a stationary
                                // pointer as they slide into their new slots.
                                const { left, width } = e.currentTarget.getBoundingClientRect();
                                const midpoint = left + width / 2;
                                if (from < to ? e.clientX > midpoint : e.clientX < midpoint) {
                                    reorder(from, to);
                                    setDragOverId(null);
                                } else if (dragOverId !== pillar.id) {
                                    setDragOverId(pillar.id);
                                }
                            }}
                            onDragLeave={() => {
                                if (dragOverId === pillar.id) setDragOverId(null);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                handleDrop(pillar.id);
                                setDraggedId(null);
                                setDragOverId(null);
                            }}
                        >
                            <span className="cw-priority__grip" aria-hidden="true">
                                <SparkIcon slug="drag-handle" size="small" />
                            </span>
                            <span className="cw-priority__card-content">
                                <span className="cw-priority__card-icon">
                                    <SparkIcon slug={pillar.iconSlug} size="medium" />
                                </span>
                                <span className="cw-priority__card-text">
                                    <span className="cw-priority__card-title">{pillar.title}</span>
                                    <span className="cw-priority__card-subtitle">
                                        {pillar.subtitle}
                                    </span>
                                </span>
                            </span>
                        </div>
                    </TooltipTrigger>
                ))}
                <div className="cw-priority__endpoint cw-priority__endpoint--end">
                    <svg
                        className="cw-priority__endpoint-arrow"
                        aria-hidden="true"
                        width="12"
                        height="22"
                        viewBox="0 0 12 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M2 1.5L9.4 11L2 20.5"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <span className="cw-priority__endpoint-label">Most Important</span>
                </div>
            </div>
        </div>
    );
};

export default PriorityRanking;
